// ============================================================================
// ITEM-WISE SALES PERFORMANCE REPORT
//
// Service revenue by Category -> Sub-category/Group -> Item for a period, with
// Sales and Credit Notes as separate figures plus Net Sales (net of tax).
// Credit notes use Transaction-Date basis (they fall in the period created).
// Document-level discounts can't be attributed to an item, so they appear only
// as a report-level "Less: Discounts" deduction.
//
// Drill: Category -> Group -> Item -> the documents behind it -> open the
// document (Sales Invoice / Credit Note) via the cross-screen nav seam — but
// ONLY when the document belongs to the logged entity/BU (the transaction
// screens work in the active X-Entity-ID / X-Business-Unit-ID context).
// ============================================================================

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import type { ActionButton } from 'components/modals';
import { SyutSelect, type SelectOption } from 'components/ui';
import { EnterpriseScreenShell, actionButtonFactory, useCan } from 'components/enterprise-shell';
import notificationService from 'services/notificationService';
import { reportService } from 'api/services';
import type {
  ItemSalesResultDto, ItemSalesRowDto, ItemSalesDetailRowDto, ItemSalesQueryParams,
} from 'api/types';
import { useAuthContext } from 'contexts/AuthContext';
import { useNavigation, canOpenInScope } from 'contexts/NavigationContext';
import ReportHeader from 'components/reports/ReportHeader';

const today = () => new Date().toISOString().slice(0, 10);
const monthStart = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); };
const fmtMoney = (n: number | null | undefined) =>
  n == null ? '—' : n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtQty = (n: number) => (n === 0 ? '' : n.toLocaleString(undefined, { maximumFractionDigits: 2 }));

interface GroupNode {
  key: string; name: string; groupId?: string | null;
  items: ItemSalesRowDto[];
  sales: number; cn: number; net: number; salesQty: number; cnQty: number; netQty: number;
}
interface CategoryNode {
  key: string; name: string; categoryId?: string | null;
  groups: GroupNode[];
  sales: number; cn: number; net: number; salesQty: number; cnQty: number; netQty: number;
}

interface ItemSalesScreenProps { moduleIcon?: string; tabId?: string; moduleName?: string; }

const ItemSalesScreen: React.FC<ItemSalesScreenProps> = ({ moduleIcon, tabId }) => {
  const can = useCan('item-sales');
  const { accessibleEntities, accessibleBusinessUnits, selectedEntity, selectedBusinessUnit } = useAuthContext();
  const { openRecord } = useNavigation();

  // --- filters ---
  const [fromDate, setFromDate] = useState<string>(monthStart());
  const [toDate, setToDate] = useState<string>(today());
  const [selectedEntityIds, setSelectedEntityIds] = useState<string[]>([]);
  const [selectedBuIds, setSelectedBuIds] = useState<string[]>([]);
  const [filterOpen, setFilterOpen] = useState(false);

  // --- applied filters: the report reflects ONLY what was applied. Editing the
  // panel changes the draft state above; clicking Apply commits it here. This
  // matches the Debtors Ageing report (changes don't take effect until Apply). ---
  const [applied, setApplied] = useState<{ fromDate: string; toDate: string; entityIds: string[]; businessUnitIds: string[] }>(
    { fromDate: monthStart(), toDate: today(), entityIds: [], businessUnitIds: [] },
  );

  // --- data ---
  const [data, setData] = useState<ItemSalesResultDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [detailFor, setDetailFor] = useState<ItemSalesRowDto | null>(null);

  const entityOptions: SelectOption[] = useMemo(
    () => accessibleEntities.map(e => ({ value: e.id, label: `${e.entityCode} — ${e.entityName}` })),
    [accessibleEntities],
  );
  const buOptions: SelectOption[] = useMemo(() => {
    const scoped = selectedEntityIds.length > 0
      ? accessibleBusinessUnits.filter(b => selectedEntityIds.includes(b.entityId))
      : accessibleBusinessUnits;
    return scoped.map(b => ({ value: b.id, label: b.buName }));
  }, [accessibleBusinessUnits, selectedEntityIds]);

  useEffect(() => {
    if (selectedEntityIds.length === 0 || selectedBuIds.length === 0) return;
    const allowed = new Set(buOptions.map(o => String(o.value)));
    const next = selectedBuIds.filter(id => allowed.has(id));
    if (next.length !== selectedBuIds.length) setSelectedBuIds(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEntityIds]);

  // The report always fetches the APPLIED filters — never the draft ones.
  const appliedParams = useCallback((): ItemSalesQueryParams => ({
    fromDate: applied.fromDate, toDate: applied.toDate,
    entityIds: applied.entityIds.length > 0 ? applied.entityIds : undefined,
    businessUnitIds: applied.businessUnitIds.length > 0 ? applied.businessUnitIds : undefined,
  }), [applied]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await reportService.getItemSales(appliedParams()));
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Could not load the report.');
      setData(null);
    } finally { setLoading(false); }
  }, [appliedParams]);

  // Fetch only when the APPLIED filters change (incl. the initial mount) — not
  // while the user is still editing the panel.
  useEffect(() => { load(); }, [load]);

  // Commit the draft filters -> applied (validates first).
  const applyFilters = useCallback(() => {
    if (!fromDate || !toDate) { notificationService.toast.warning('Period (From / To) is required.'); return; }
    if (fromDate > toDate) { notificationService.toast.warning('From date must be on or before To date.'); return; }
    setApplied({ fromDate, toDate, entityIds: selectedEntityIds, businessUnitIds: selectedBuIds });
    setFilterOpen(false);
  }, [fromDate, toDate, selectedEntityIds, selectedBuIds]);

  // Unapplied-change detection -> stale ribbon (mirrors Debtors Ageing).
  const isStale = useMemo(
    () => JSON.stringify({ f: fromDate, t: toDate, e: selectedEntityIds, b: selectedBuIds })
       !== JSON.stringify({ f: applied.fromDate, t: applied.toDate, e: applied.entityIds, b: applied.businessUnitIds }),
    [fromDate, toDate, selectedEntityIds, selectedBuIds, applied],
  );

  const handleExport = useCallback(async () => {
    try { await reportService.exportItemSales(appliedParams()); notificationService.toast.success('Export downloaded.'); }
    catch (err: any) { notificationService.toast.error(err?.response?.data?.message || 'Could not export.'); }
  }, [appliedParams]);

  // Build the Category -> Group tree with subtotals from the flat rows.
  const tree: CategoryNode[] = useMemo(() => {
    const cats = new Map<string, CategoryNode>();
    for (const r of data?.rows ?? []) {
      const catKey = r.categoryCode || 'UNCATEGORIZED';
      let cat = cats.get(catKey);
      if (!cat) {
        cat = { key: catKey, name: r.categoryName, categoryId: r.categoryId, groups: [], sales: 0, cn: 0, net: 0, salesQty: 0, cnQty: 0, netQty: 0 };
        cats.set(catKey, cat);
      }
      const grpKey = r.groupName ?? '(direct)';
      let grp = cat.groups.find(g => g.key === grpKey);
      if (!grp) { grp = { key: grpKey, name: grpKey, groupId: r.groupId, items: [], sales: 0, cn: 0, net: 0, salesQty: 0, cnQty: 0, netQty: 0 }; cat.groups.push(grp); }
      grp.items.push(r);
      for (const node of [cat, grp]) {
        node.sales += r.salesAmount; node.cn += r.creditNoteAmount; node.net += r.netSalesAmount;
        node.salesQty += r.salesQty; node.cnQty += r.creditNoteQty; node.netQty += r.netQty;
      }
    }
    return Array.from(cats.values());
  }, [data]);

  const toggle = (key: string) => setExpanded(prev => {
    const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n;
  });

  const actionButtons: ActionButton[] = [
    { ...actionButtonFactory.refresh(load) },
    ...(can('EXPORT') ? [{ id: 'export', icon: <IconSystem.download size={16} />, label: 'Export', onClick: handleExport, variant: 'icon' as const }] : []),
  ];

  const ccy = data?.baseCurrencyCode || '';
  const appliedCount = (applied.entityIds.length ? 1 : 0) + (applied.businessUnitIds.length ? 1 : 0);
  const money = (v: number) => (
    <span style={{ display: 'block', textAlign: 'right', color: v < 0 ? 'var(--danger-color, #dc2626)' : undefined }}>{fmtMoney(v)}</span>
  );

  return (
    <EnterpriseScreenShell
      title="Item-wise Sales"
      description="Service sales performance by category, sub-category and item"
      icon={moduleIcon && (IconSystem as any)[moduleIcon]
        ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 })
        : <IconSystem.file size={18} />}
      tabId={tabId}
      actionButtons={actionButtons}
      filterConfig={{ onClick: () => setFilterOpen(!filterOpen), isActive: filterOpen, appliedCount }}
      trailingContent={
        filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters">
                <IconSystem.x size={14} />
              </button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>From <span className="text-danger">*</span></label>
                <input type="date" className="form-input" value={fromDate} onChange={e => setFromDate(e.target.value)}
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>To <span className="text-danger">*</span></label>
                <input type="date" className="form-input" value={toDate} onChange={e => setToDate(e.target.value)}
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              {entityOptions.length > 1 && (
                <div className="filter-section">
                  <SyutSelect label="Entities" isMulti options={entityOptions} value={selectedEntityIds}
                    onChange={v => setSelectedEntityIds((v as string[]) || [])}
                    placeholder="All my entities" size="sm" menuPortalTarget={document.body} />
                </div>
              )}
              <div className="filter-section">
                <SyutSelect label="Business Units" isMulti options={buOptions} value={selectedBuIds}
                  onChange={v => setSelectedBuIds((v as string[]) || [])}
                  placeholder="All business units" size="sm" menuPortalTarget={document.body} />
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={() => {
                setSelectedEntityIds([]); setSelectedBuIds([]); setFromDate(monthStart()); setToDate(today());
              }}>Clear</button>
              <button className="btn btn-primary" onClick={applyFilters}>Apply</button>
            </div>
          </div>
        )
      }
    >
      <ReportHeader
        filters={[
          { label: 'Period', value: `${applied.fromDate} → ${applied.toDate}` },
          { label: 'Entities', value: applied.entityIds.length ? `${applied.entityIds.length} selected` : 'All' },
          { label: 'Business Units', value: applied.businessUnitIds.length ? `${applied.businessUnitIds.length} selected` : 'All' },
          { label: `Sales ${ccy}`, value: data?.mixedBaseCurrency ? 'Mixed' : fmtMoney(data?.totalSales) },
          { label: `Credit Notes ${ccy}`, value: data?.mixedBaseCurrency ? 'Mixed' : fmtMoney(data?.totalCreditNotes) },
          { label: `Net (of tax & disc.) ${ccy}`, value: data?.mixedBaseCurrency ? 'Mixed' : fmtMoney(data?.netSalesAfterDiscount) },
        ]}
      />

      {data?.mixedBaseCurrency && (
        <div style={{ margin: '0.5rem 1rem', padding: '0.5rem 0.75rem', borderRadius: '0.5rem', background: 'var(--warning-bg, #fef3c7)', color: 'var(--warning-text, #92400e)', fontSize: '0.8125rem' }}>
          The selected entities use different functional currencies, so totals are not combined.
          Filter to entities sharing one base currency to see totals.
        </div>
      )}

      {isStale && (
        <div style={{
          margin: '0.5rem 1rem', padding: '0.4rem 0.75rem', borderRadius: '0.5rem',
          background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a',
          fontSize: '0.8125rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.5rem',
        }}>
          <IconSystem.info size={14} />
          <span>Filters changed — click <strong>Apply</strong> to refresh the report.</span>
        </div>
      )}

      <div className="data-grid-container" style={{ overflow: 'auto', padding: '0 1rem' }}>
        <table className="syut-grid" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th style={{ minWidth: 320 }}>Category / Group / Item</th>
              <th style={{ textAlign: 'right' }}>Sales Qty</th>
              <th style={{ textAlign: 'right' }}>Sales</th>
              <th style={{ textAlign: 'right' }}>Credit Notes</th>
              <th style={{ textAlign: 'right' }}>Net Sales</th>
            </tr>
          </thead>
          <tbody>
            {tree.length === 0 && (
              <tr><td colSpan={5} style={{ textAlign: 'center', padding: '1.5rem', color: 'var(--text-muted)' }}>
                {loading ? 'Loading...' : 'No sales for the selected period.'}
              </td></tr>
            )}
            {tree.map(cat => {
              const catOpen = expanded.has(cat.key);
              return (
                <React.Fragment key={cat.key}>
                  <tr style={{ background: 'var(--bg-secondary, #f1f5f9)', cursor: 'pointer', fontWeight: 700 }} onClick={() => toggle(cat.key)}>
                    <td>{catOpen ? '▾' : '▸'} {cat.name}</td>
                    <td style={{ textAlign: 'right' }}>{fmtQty(cat.salesQty)}</td>
                    <td>{money(cat.sales)}</td>
                    <td>{money(cat.cn)}</td>
                    <td>{money(cat.net)}</td>
                  </tr>
                  {catOpen && cat.groups.map(grp => {
                    const gKey = `${cat.key}::${grp.key}`;
                    const gOpen = expanded.has(gKey);
                    return (
                      <React.Fragment key={gKey}>
                        <tr style={{ cursor: 'pointer', fontWeight: 600 }} onClick={() => toggle(gKey)}>
                          <td style={{ paddingLeft: '1.75rem' }}>{gOpen ? '▾' : '▸'} {grp.name}</td>
                          <td style={{ textAlign: 'right' }}>{fmtQty(grp.salesQty)}</td>
                          <td>{money(grp.sales)}</td>
                          <td>{money(grp.cn)}</td>
                          <td>{money(grp.net)}</td>
                        </tr>
                        {gOpen && grp.items.map(item => (
                          <tr key={item.serviceId ?? `${gKey}-unmapped`}>
                            <td style={{ paddingLeft: '3.25rem' }}>
                              {item.serviceId ? (
                                <span role="button" tabIndex={0}
                                  onClick={() => setDetailFor(item)}
                                  onKeyDown={e => { if (e.key === 'Enter') setDetailFor(item); }}
                                  style={{ color: 'var(--primary-color, #2563eb)', cursor: 'pointer' }}
                                  title="Show the documents behind this item">
                                  {item.serviceName || item.serviceCode || '(unspecified)'}
                                </span>
                              ) : (item.serviceName || '(unspecified)')}
                            </td>
                            <td style={{ textAlign: 'right' }}>{fmtQty(item.salesQty)}</td>
                            <td>{money(item.salesAmount)}</td>
                            <td>{money(item.creditNoteAmount)}</td>
                            <td>{money(item.netSalesAmount)}</td>
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </tbody>
          {data && !data.mixedBaseCurrency && tree.length > 0 && (
            <tfoot>
              <tr style={{ fontWeight: 600, borderTop: '2px solid var(--border-color)' }}>
                <td>Total Sales / Credit Notes / Net</td>
                <td></td>
                <td>{money(data.totalSales)}</td>
                <td>{money(data.totalCreditNotes)}</td>
                <td>{money(data.netSales)}</td>
              </tr>
              <tr>
                <td style={{ color: 'var(--text-muted)' }}>Less: Discounts (document-level, not item-attributable)</td>
                <td colSpan={3}></td>
                <td>{money(data.discountTotal)}</td>
              </tr>
              <tr style={{ fontWeight: 700 }}>
                <td>Net Sales (of tax and discounts)</td>
                <td colSpan={3}></td>
                <td>{money(data.netSalesAfterDiscount)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {detailFor && (
        <ItemDetailModal
          item={detailFor}
          params={appliedParams()}
          activeEntityId={selectedEntity?.id}
          activeBuId={selectedBusinessUnit?.id}
          onOpenDocument={(screenCode, id) => { openRecord(screenCode, { recordId: id, mode: 'edit' }); setDetailFor(null); }}
          onClose={() => setDetailFor(null)}
        />
      )}
    </EnterpriseScreenShell>
  );
};

// ---------------------------------------------------------------------------
// ITEM DETAIL — documents behind one service item; open gated by active scope.
// ---------------------------------------------------------------------------

const ItemDetailModal: React.FC<{
  item: ItemSalesRowDto;
  params: ItemSalesQueryParams;
  activeEntityId?: string;
  activeBuId?: string;
  onOpenDocument: (screenCode: string, documentId: string) => void;
  onClose: () => void;
}> = ({ item, params, activeEntityId, activeBuId, onOpenDocument, onClose }) => {
  const [rows, setRows] = useState<ItemSalesDetailRowDto[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await reportService.getItemSalesDetail(item.serviceId!, params);
        if (!cancelled) setRows(r);
      } catch (err: any) {
        if (!cancelled) { notificationService.toast.error(err?.response?.data?.message || 'Could not load details.'); setRows([]); }
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.serviceId]);

  const fmt = (n: number) => n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div className="modal-container modal-xl">
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <div>
            <h2 className="modal-title">{item.serviceName || item.serviceCode}</h2>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
              Documents · {params.fromDate} to {params.toDate}
            </div>
          </div>
          <button className="modal-close" onClick={onClose}><IconSystem.x size={20} /></button>
        </div>
        <div className="modal-content">
          {loading && <div style={{ padding: '1rem', color: 'var(--text-secondary)' }}>Loading...</div>}
          {!loading && (
            <div style={{ maxHeight: '60vh', overflow: 'auto' }}>
              <table className="syut-grid" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th>Type</th><th>Document</th><th>Date</th><th>Customer</th><th>Entity / BU</th>
                    <th style={{ textAlign: 'right' }}>Qty</th>
                    <th style={{ textAlign: 'right' }}>Net Amount</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && (
                    <tr><td colSpan={8} style={{ textAlign: 'center', padding: '1rem', color: 'var(--text-muted)' }}>No documents.</td></tr>
                  )}
                  {rows.map(r => {
                    const canOpen = canOpenInScope(r.entityId, r.businessUnitId, activeEntityId, activeBuId);
                    return (
                      <tr key={r.documentLineId}>
                        <td>{r.kind === 'CREDIT_NOTE' ? 'Credit Note' : 'Invoice'}</td>
                        <td>{r.documentNo}</td>
                        <td>{(r.documentDate || '').slice(0, 10)}</td>
                        <td>{r.partyName || '—'}</td>
                        <td>{r.entityCode}{r.businessUnitName ? ` / ${r.businessUnitName}` : ''}</td>
                        <td style={{ textAlign: 'right' }}>{r.quantity ? fmt(r.quantity) : ''}</td>
                        <td style={{ textAlign: 'right', color: r.netAmount < 0 ? 'var(--danger-color,#dc2626)' : undefined }}>{fmt(r.netAmount)}</td>
                        <td style={{ textAlign: 'right' }}>
                          <button className="btn btn-secondary" style={{ padding: '0.2rem 0.55rem', fontSize: '0.75rem' }}
                            disabled={!canOpen}
                            title={canOpen ? 'Open the document' : `Switch to ${r.entityCode}${r.businessUnitName ? ' / ' + r.businessUnitName : ''} to open this document`}
                            onClick={() => onOpenDocument(r.screenCode, r.documentId)}>
                            Open
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>
                Documents outside the entity/business unit you're currently working in can't be opened directly —
                switch to that scope from the top bar to open them.
              </div>
            </div>
          )}
        </div>
        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </>
  );
};

export default ItemSalesScreen;
